import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Car, Loader2, Pencil, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CustomerAPI, AuditAPI, extractError } from "@/lib/api";
import { VehicleCard } from "@/components/VehicleCard";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

export default function CustomerDetailPage() {
    const { id } = useParams();
    const { permissions } = useAuth();
    const canUpdate = permissions.includes("customer:update");
    const canDelete = permissions.includes("customer:delete");
    const canCreateVehicle = permissions.includes("vehicle:create");
    const canReadAudit = permissions.includes("audit:read");
    const nav = useNavigate();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [audit, setAudit] = useState({ items: [] });

    async function load() {
        setLoading(true);
        try {
            const c = await CustomerAPI.get(id);
            setData(c);
            if (canReadAudit) {
                const a = await AuditAPI.list({ entity: "Customer", limit: 20 });
                setAudit({ items: a.items.filter((x) => x.entity_id === id) });
            }
        } catch (e) {
            toast.error(extractError(e));
            if (e?.response?.status === 404) nav("/customers");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    async function onDelete() {
        try {
            await CustomerAPI.remove(id);
            toast.success("Customer deleted");
            nav("/customers");
        } catch (e) {
            toast.error(extractError(e));
        }
    }

    if (loading) return <div className="text-sm text-zinc-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…</div>;
    if (!data) return null;

    return (
        <div className="space-y-6 animate-fade-in" data-testid="customer-detail">
            <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                        {data.customer_code} · {data.customer_type}
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">{data.display_name}</h1>
                    <div className="mt-1 font-mono text-sm text-zinc-500">{data.phone}{data.email && <> · {data.email}</>}</div>
                </div>
                <div className="flex items-center gap-2">
                    {canUpdate && (
                        <Link to={`/customers/${id}/edit`}>
                            <Button variant="outline" data-testid="customer-edit-btn"><Pencil className="mr-1 h-4 w-4" /> Edit</Button>
                        </Link>
                    )}
                    {canDelete && (
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="outline" className="text-rose-600" data-testid="customer-delete-btn">
                                    <Trash2 className="mr-1 h-4 w-4" /> Delete
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Delete customer?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Soft-deletes <span className="font-mono">{data.customer_code}</span>. Their record stays for audit. Active vehicles must be removed first.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction className="bg-rose-600 text-white hover:bg-rose-700" onClick={onDelete} data-testid="customer-delete-confirm">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    )}
                </div>
            </header>

            <Tabs defaultValue="overview">
                <TabsList>
                    <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                    <TabsTrigger value="vehicles" data-testid="tab-vehicles">Vehicles ({data.vehicles?.length || 0})</TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
                    {canReadAudit && <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>}
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                    <Card className="p-6">
                        <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Contact</h2>
                        <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                            <Row label="Phone" value={data.phone} mono />
                            <Row label="Alt phone" value={data.alt_phone || "—"} mono />
                            <Row label="Email" value={data.email || "—"} />
                            <Row label="GSTIN" value={data.gstin || "—"} mono />
                            <Row label="Tags" value={(data.tags || []).join(", ") || "—"} />
                            <Row label="Loyalty points" value={data.loyalty_points ?? 0} mono />
                        </dl>
                    </Card>

                    {data.address && (
                        <Card className="p-6">
                            <h2 className="mb-4 font-display text-lg font-bold tracking-tight">Address</h2>
                            <div className="text-sm text-zinc-700 dark:text-zinc-300">
                                {[data.address.line1, data.address.line2, data.address.city, `${data.address.state_code} ${data.address.postal_code}`, data.address.country_code]
                                    .filter(Boolean).join(", ")}
                            </div>
                        </Card>
                    )}

                    {data.notes && (
                        <Card className="p-6">
                            <h2 className="mb-2 font-display text-lg font-bold tracking-tight">Notes</h2>
                            <p className="whitespace-pre-wrap text-sm text-zinc-700 dark:text-zinc-300">{data.notes}</p>
                        </Card>
                    )}
                </TabsContent>

                <TabsContent value="vehicles">
                    <Card className="p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="font-display text-lg font-bold tracking-tight">Vehicles</h2>
                            {canCreateVehicle && (
                                <Link to={`/vehicles/new?customer_id=${id}`}>
                                    <Button className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="add-vehicle-btn">Add vehicle</Button>
                                </Link>
                            )}
                        </div>
                        {(!data.vehicles || data.vehicles.length === 0) ? (
                            <EmptyState
                                icon={Car}
                                title="No vehicles attached"
                                description="Add a vehicle to start tracking service history and reminders."
                                action={canCreateVehicle && <Link to={`/vehicles/new?customer_id=${id}`}><Button className="bg-brand-red text-white hover:bg-brand-redDeep">Add vehicle</Button></Link>}
                            />
                        ) : (
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {data.vehicles.map((v) => <VehicleCard key={v.id} vehicle={v} />)}
                            </div>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="history">
                    <EmptyState
                        title="No service history yet"
                        description="Service history will populate once jobs are completed for this customer's vehicles."
                    />
                </TabsContent>

                {canReadAudit && (
                    <TabsContent value="audit">
                        <Card className="overflow-hidden">
                            <div className="p-4">
                                <h2 className="font-display text-lg font-bold tracking-tight">Audit trail</h2>
                            </div>
                            {audit.items.length === 0 ? (
                                <div className="p-6 text-center text-sm text-zinc-500">No entries.</div>
                            ) : (
                                <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
                                    {audit.items.map((it) => (
                                        <li key={it.id} className="flex items-start gap-3 px-4 py-3 text-sm">
                                            <span className="rounded border border-brand-redSoft bg-brand-redSoft px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-brand-redDeep dark:border-brand-red/40 dark:bg-brand-red/10 dark:text-brand-red">{it.action}</span>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-mono text-[11px] text-zinc-500">{formatDateTime(it.created_at)}</div>
                                                {it.diff && (
                                                    <div className="mt-1 text-xs text-zinc-700 dark:text-zinc-300">
                                                        Changed: {Object.keys(it.diff).filter(k => !["updated_at", "version"].includes(k)).join(", ") || "—"}
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Card>
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex flex-col gap-0.5">
            <dt className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{label}</dt>
            <dd className={mono ? "font-mono text-sm" : "text-sm"}>{value}</dd>
        </div>
    );
}
